/**
 * Owner: Masa Bayu
 * File: text-authoring-language.ts
 * Date:Time: 2026-09-20 22:40
 * Description: Phase 7 CA-07 language/RTL/typography enforcement — no new semantics.
 */
import { QXW_TEXT_LANGUAGE_FIELDS } from './schema/fields';
import { assertQXTextLanguageCode, bindQXTextTypography, deriveQXTextDirection, resolveQXTextLanguage, } from './text-language';
/** Keys forbidden on ContentNode (CA-07 L3/L4) — presentation never QXW SoT. */
const TYPOGRAPHY_BAG_KEYS = [
    'direction',
    'dir',
    'fontFamily',
    'fontSize',
    'fontWeight',
    'lineHeight',
    'writingMode',
    'unicodeBidi',
    'bidi',
    'letterSpacing',
    'textAlign',
    'font',
    'cssFont',
];
const LANGUAGE_ALLOWED = new Set(QXW_TEXT_LANGUAGE_FIELDS);
function validateLanguageNode(value, path, out) {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        out.push({
            code: 'language-bag',
            severity: 'error',
            path,
            message: 'language must be { languageCode } (CA-07 L1).',
        });
        return null;
    }
    const record = value;
    for (const key of Object.keys(record)) {
        if (!LANGUAGE_ALLOWED.has(key)) {
            out.push({
                code: 'language-bag',
                severity: 'error',
                path: `${path}.${key}`,
                message: `forbidden language field "${key}" (CA-07 L1 · no direction/locale bags).`,
            });
        }
    }
    const raw = record.languageCode;
    if (typeof raw !== 'string' || raw.length === 0) {
        out.push({
            code: 'invalid-language-code',
            severity: 'error',
            path: `${path}.languageCode`,
            message: 'languageCode required and must be a non-empty string.',
        });
        return null;
    }
    let canonical;
    try {
        canonical = assertQXTextLanguageCode(raw);
    }
    catch (error) {
        const message = error instanceof Error ? error.message : 'invalid languageCode';
        out.push({
            code: message.includes('unsupported')
                ? 'unsupported-language'
                : 'invalid-language-code',
            severity: 'error',
            path: `${path}.languageCode`,
            message,
        });
        return null;
    }
    try {
        deriveQXTextDirection(canonical);
    }
    catch (error) {
        out.push({
            code: 'ambiguous-direction',
            severity: 'error',
            path,
            message: error instanceof Error
                ? error.message
                : `ambiguous language-direction state for: ${canonical}`,
        });
        return null;
    }
    try {
        const typography = bindQXTextTypography(canonical, 'body');
        if (typeof typography.primaryFontFamilyId !== 'string' ||
            typography.primaryFontFamilyId.length === 0) {
            out.push({
                code: 'typography-binding',
                severity: 'error',
                path,
                message: 'QXTypographySystem returned empty binding (CA-07 L4).',
            });
        }
    }
    catch (error) {
        out.push({
            code: 'typography-binding',
            severity: 'error',
            path,
            message: error instanceof Error
                ? error.message
                : 'typography binding failed (CA-07 L4).',
        });
    }
    return canonical;
}
function collectTypographyBags(value, path, out) {
    if (value === null || typeof value !== 'object') {
        return;
    }
    if (Array.isArray(value)) {
        value.forEach((item, index) => {
            collectTypographyBags(item, `${path}[${String(index)}]`, out);
        });
        return;
    }
    const record = value;
    for (const key of Object.keys(record)) {
        if (TYPOGRAPHY_BAG_KEYS.includes(key)) {
            out.push({
                code: 'typography-bag',
                severity: 'error',
                path: `${path}.${key}`,
                message: `typography/direction field "${key}" forbidden on content (CA-07 L3/L4).`,
            });
        }
    }
    for (const [key, child] of Object.entries(record)) {
        if (key === 'language') {
            continue;
        }
        collectTypographyBags(child, `${path}.${key}`, out);
    }
}
function walkLanguageFields(value, path, out) {
    if (value === null || typeof value !== 'object') {
        return;
    }
    if (Array.isArray(value)) {
        value.forEach((item, index) => {
            walkLanguageFields(item, `${path}[${String(index)}]`, out);
        });
        return;
    }
    const record = value;
    if ('language' in record && record.language !== undefined) {
        validateLanguageNode(record.language, `${path}.language`, out);
    }
    for (const [key, child] of Object.entries(record)) {
        if (key === 'language') {
            continue;
        }
        walkLanguageFields(child, `${path}.${key}`, out);
    }
}
/**
 * Verify L2 inheritance: explicit wins; caption inherits figure parent.
 * Fail-closed when resolution throws (unsupported / ambiguous).
 */
function checkInheritance(content, documentLanguageCode, out) {
    const contentCode = typeof content.language?.languageCode === 'string'
        ? content.language.languageCode
        : null;
    content.blocks.forEach((block, index) => {
        const path = `content.blocks[${String(index)}]`;
        try {
            const resolved = resolveQXTextLanguage({
                explicit: block.type === 'rule' ? null : (block.language ?? null),
                contentLanguageCode: contentCode,
                documentLanguageCode: documentLanguageCode ?? null,
            });
            if (block.type !== 'rule' && block.language !== undefined && resolved.source !== 'explicit') {
                out.push({
                    code: 'inheritance-conflict',
                    severity: 'error',
                    path: `${path}.language`,
                    message: 'explicit language must win inheritance (CA-07 L2).',
                });
            }
            if (block.type !== 'rule' &&
                block.language === undefined &&
                contentCode !== null &&
                resolved.source !== 'content' &&
                documentLanguageCode == null) {
                out.push({
                    code: 'inheritance-conflict',
                    severity: 'error',
                    path,
                    message: 'block must inherit content language when no explicit (CA-07 L2).',
                });
            }
            if (block.type === 'figure' && block.caption !== undefined) {
                const captionResolved = resolveQXTextLanguage({
                    explicit: block.caption.language ?? null,
                    parentLanguageCode: resolved.languageCode,
                    contentLanguageCode: contentCode,
                    documentLanguageCode: documentLanguageCode ?? null,
                });
                if (block.caption.language === undefined &&
                    resolved.languageCode !== null &&
                    captionResolved.source !== 'parent' &&
                    captionResolved.languageCode !== resolved.languageCode) {
                    out.push({
                        code: 'inheritance-conflict',
                        severity: 'error',
                        path: `${path}.caption`,
                        message: 'caption must inherit figure language when no explicit (CA-07 L2).',
                    });
                }
            }
        }
        catch (error) {
            out.push({
                code: 'ambiguous-direction',
                severity: 'error',
                path,
                message: error instanceof Error
                    ? error.message
                    : 'language inheritance resolution failed.',
            });
        }
    });
}
/**
 * CA-07 language / RTL / typography integrity.
 * Aggregates identity · bags · direction · typography binding · inheritance.
 */
export function validateQXTextLanguage(content, options) {
    const issues = [];
    collectTypographyBags(content, 'content', issues);
    walkLanguageFields(content, 'content', issues);
    if (content !== null &&
        typeof content === 'object' &&
        !Array.isArray(content) &&
        Array.isArray(content.blocks)) {
        checkInheritance(content, options.documentLanguageCode, issues);
    }
    // mode reserved for publish-strict extensions; v1 fail-closed is already error-severity.
    void options.mode;
    return issues;
}
export function qxTextLanguageHasErrors(issues) {
    return issues.some((issue) => issue.severity === 'error');
}
//# sourceMappingURL=text-authoring-language.js.map